import { useState, useEffect, useCallback, useRef } from "react";
import {
  Phone,
  PhoneOff,
  Volume2,
  VolumeX,
  Loader2,
  X,
  Clock,
  Mic,
  MicOff,
} from "lucide-react";
import { useRealtimeVoice } from "@/hooks/useRealtimeVoice";
import { getPersona } from "@shared/persona";

interface VoiceChatOverlayProps {
  clientId: string;
  voicePreference: "annam" | "christopher";
  onClose: () => void;
  onVoicePreferenceChange?: (pref: "annam" | "christopher") => void;
  sessionType?: string;
  sessionTitle?: string;
  continueSessionId?: number | null;
  onTranscriptCommit?: (entry: {
    id: string;
    role: "user" | "assistant";
    text: string;
  }) => void;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export default function VoiceChatOverlay({
  clientId,
  voicePreference,
  onClose,
  onVoicePreferenceChange,
  sessionType,
  sessionTitle,
  continueSessionId,
  onTranscriptCommit,
}: VoiceChatOverlayProps) {
  const personaName = getPersona(voicePreference).name;
  const {
    status,
    isSpeaking,
    micMuted,
    transcript,
    error: hookError,
    start,
    stop,
    toggleMic,
    interrupt,
    setOutputVolume,
  } = useRealtimeVoice();

  const [durationSeconds, setDurationSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [speakerMuted, setSpeakerMuted] = useState(false);

  const startedAtRef = useRef<number | null>(null);
  const connectedOnceRef = useRef(false);
  const closingRef = useRef(false);
  const emittedTranscriptIdsRef = useRef<Set<string>>(new Set());
  const mobileTranscriptScrollRef = useRef<HTMLDivElement>(null);
  const mobileTranscriptEndRef = useRef<HTMLDivElement>(null);

  const isConnecting = status === "connecting";
  const isConnected =
    status === "listening" || status === "processing" || status === "speaking";
  const isActive = isConnecting || isConnected;

  useEffect(() => {
    if (!onTranscriptCommit) return;

    for (const entry of transcript) {
      if (!entry.isFinal) continue;
      if (emittedTranscriptIdsRef.current.has(entry.id)) continue;

      const text = entry.text.trim();
      if (!text) continue;

      emittedTranscriptIdsRef.current.add(entry.id);
      onTranscriptCommit({
        id: entry.id,
        role: entry.role,
        text,
      });
    }
  }, [onTranscriptCommit, transcript]);

  useEffect(() => {
    if (!isConnected) return;

    if (!connectedOnceRef.current) {
      connectedOnceRef.current = true;
      startedAtRef.current = Date.now();
      setDurationSeconds(0);
    }

    const interval = setInterval(() => {
      const startedAt = startedAtRef.current;
      if (!startedAt) return;
      setDurationSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [isConnected]);

  useEffect(() => {
    if (status === "listening") {
      setError(null);
      return;
    }

    if (status === "error") {
      setError(
        hookError ||
          "Die Sprachverbindung wurde unterbrochen. Bitte versuche es erneut."
      );
    }
  }, [status, hookError]);

  useEffect(() => {
    const entries = transcript
      .filter(entry => entry.text.trim().length > 0)
      .slice(-10);

    if (!entries.length) return;

    requestAnimationFrame(() => {
      mobileTranscriptEndRef.current?.scrollIntoView({
        behavior: entries.length > 1 ? "smooth" : "auto",
        block: "end",
      });

      const container = mobileTranscriptScrollRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    });
  }, [transcript]);

  const finalizeAndClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    onClose();
  }, [onClose]);

  const startConversation = useCallback(async () => {
    closingRef.current = false;
    connectedOnceRef.current = false;
    emittedTranscriptIdsRef.current.clear();
    startedAtRef.current = null;
    setDurationSeconds(0);
    setError(null);
    setSpeakerMuted(false);
    setOutputVolume(1);

    try {
      await start({
        clientId,
        voiceId: voicePreference,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(`Konnte Voice Coach nicht starten: ${msg}`);
    }
  }, [clientId, voicePreference, setOutputVolume, start]);

  useEffect(() => {
    void startConversation();

    return () => {
      closingRef.current = true;
      stop();
    };
  }, [startConversation, stop]);

  useEffect(() => {
    if (status === "idle" && connectedOnceRef.current) {
      finalizeAndClose();
    }
  }, [finalizeAndClose, status]);

  const endConversation = useCallback(() => {
    stop();
    finalizeAndClose();
  }, [finalizeAndClose, stop]);

  const handleOverlayClose = useCallback(() => {
    stop();
    finalizeAndClose();
  }, [finalizeAndClose, stop]);

  const toggleSpeaker = useCallback(() => {
    const nextMuted = !speakerMuted;
    setSpeakerMuted(nextMuted);
    setOutputVolume(nextMuted ? 0 : 1);
  }, [setOutputVolume, speakerMuted]);

  const statusTitle = isConnecting
    ? "Verbindung wird hergestellt..."
    : status === "processing"
      ? `${personaName} denkt nach...`
      : isSpeaking
        ? `${personaName} spricht`
        : micMuted
          ? "Mikrofon stumm"
          : `${personaName} hört dir zu`;

  const statusDescription = isConnecting
    ? "Bitte warte einen Moment..."
    : status === "processing"
      ? "Deine Nachricht wird verarbeitet..."
      : isSpeaking
        ? `${personaName} antwortet dir. Du kannst unterbrechen, indem du sprichst.`
        : micMuted
          ? "Tippe das Mikrofon-Icon um wieder zu sprechen."
          : "Sprich frei. Das Transkript erscheint direkt im Chat hinter dem Popup.";

  const mobileTranscriptEntries = transcript
    .filter(entry => entry.text.trim().length > 0)
    .slice(-10);

  return (
    <div className="fixed inset-0 z-50 animate-in fade-in duration-300">
      <div className="absolute inset-0 bg-[#1a2410]/35 backdrop-blur-[2px]" />

      <div className="relative flex h-full items-center justify-center px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex max-h-[calc(100vh-3rem)] w-full max-w-[460px] flex-col overflow-hidden rounded-[2rem] bg-gradient-to-b from-[#FAF7F4] to-white p-6 text-center shadow-2xl ring-1 ring-black/5 sm:p-8">
          <div className="mb-6 flex items-start justify-between gap-4 text-left">
            <div></div>
            <button
              onClick={handleOverlayClose}
              className="shrink-0 rounded-full border border-[#d9dfcf] bg-white/80 p-2 text-[#5f6f53] transition-all hover:border-[#b8c5a6] hover:text-[#2d3a1e]"
              title="Gespräch schließen"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mb-6 flex items-center justify-center gap-2 text-xs text-[#5f6f53]">
            <Clock className="h-3.5 w-3.5" />
            <span className="font-mono">{formatDuration(durationSeconds)}</span>
          </div>

          <div className="relative mx-auto mb-8 h-32 w-32">
            <div
              className={`absolute inset-0 rounded-full border-2 transition-all duration-500 ${
                isSpeaking
                  ? "scale-110 animate-pulse border-[#6b7f4e]/40"
                  : "scale-100 border-[#6b7f4e]/20"
              }`}
            />
            <div
              className={`absolute inset-3 rounded-full border transition-all duration-700 ${
                isSpeaking
                  ? "scale-105 border-[#6b7f4e]/30"
                  : "scale-100 border-[#6b7f4e]/10"
              }`}
            />
            <div
              className={`absolute inset-6 flex items-center justify-center rounded-full transition-all duration-300 ${
                isSpeaking
                  ? "bg-gradient-to-br from-[#6b7f4e] to-[#8ba366] shadow-lg shadow-[#6b7f4e]/30"
                  : "bg-gradient-to-br from-[#6b7f4e]/80 to-[#8ba366]/80"
              }`}
            >
              {isConnecting ? (
                <Loader2 className="h-8 w-8 animate-spin text-white" />
              ) : isSpeaking ? (
                <Volume2 className="h-8 w-8 animate-pulse text-white" />
              ) : micMuted ? (
                <MicOff className="h-8 w-8 text-white/70" />
              ) : (
                <Phone className="h-8 w-8 text-white/85" />
              )}
            </div>
          </div>

          <div className="mb-6">
            <h3 className="mb-1 text-lg font-semibold text-[#2d3a1e]">
              {statusTitle}
            </h3>
            <p className="text-xs text-[#5f6f53]">{statusDescription}</p>
          </div>

          {error && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-2 text-xs text-red-600">
              {error}
              <button
                onClick={() => {
                  setError(null);
                  stop();
                  void startConversation();
                }}
                className="ml-2 underline hover:no-underline"
              >
                Erneut versuchen
              </button>
            </div>
          )}

          <div className="mb-5 w-full max-w-[340px] mx-auto rounded-[1.5rem] border border-[#d9dfcf] bg-white/85 p-3 text-left shadow-sm lg:hidden">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8ba366]">
              Live-Transkript
            </p>
            <div
              ref={mobileTranscriptScrollRef}
              className="h-[220px] w-full max-w-[380px] mx-auto space-y-2 overflow-y-auto pr-1"
            >
              {mobileTranscriptEntries.length > 0 ? (
                mobileTranscriptEntries.map(entry => (
                  <div
                    key={entry.id}
                    className={`rounded-2xl px-3 py-2 text-xs leading-relaxed ${
                      entry.role === "assistant"
                        ? "bg-[#eef2e6] text-[#2d3a1e]"
                        : "bg-[#f8f8f8] text-[#5f6f53]"
                    }`}
                  >
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8a9680]">
                      {entry.role === "assistant" ? personaName : "Du"}
                    </p>
                    <p className={entry.isFinal ? "" : "opacity-70"}>
                      {entry.text}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-xs leading-relaxed text-[#7b866f]">
                  Sobald ihr sprecht, erscheint das laufende Transkript hier
                  direkt im mobilen Call-Fenster.
                </p>
              )}
              <div ref={mobileTranscriptEndRef} />
            </div>
          </div>

          <div className="flex items-center justify-center gap-4">
            <button
              onClick={toggleSpeaker}
              disabled={!isActive}
              className={`flex h-12 w-12 items-center justify-center rounded-full transition-all ${
                speakerMuted
                  ? "bg-yellow-100 text-yellow-600"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              } ${!isActive ? "cursor-not-allowed opacity-50" : ""}`}
              title={speakerMuted ? "Ton einschalten" : "Ton ausschalten"}
            >
              {speakerMuted ? (
                <VolumeX className="h-5 w-5" />
              ) : (
                <Volume2 className="h-5 w-5" />
              )}
            </button>

            <button
              onClick={toggleMic}
              disabled={!isActive}
              className={`flex h-12 w-12 items-center justify-center rounded-full transition-all ${
                micMuted
                  ? "bg-red-100 text-red-500"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              } ${!isActive ? "cursor-not-allowed opacity-50" : ""}`}
              title={
                micMuted ? "Mikrofon einschalten" : "Mikrofon stummschalten"
              }
            >
              {micMuted ? (
                <MicOff className="h-5 w-5" />
              ) : (
                <Mic className="h-5 w-5" />
              )}
            </button>

            <button
              onClick={endConversation}
              className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500 text-white shadow-lg transition-all duration-200 hover:scale-105 hover:bg-red-600"
              title="Gespräch beenden"
            >
              <PhoneOff className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
